import {
  type CSSProperties,
  type DOMAttributes,
  Fragment,
  type JSX,
  type MouseEventHandler,
  type MutableRefObject,
  type Ref,
} from 'react';
import { FONT_FAMILY, FONT_SIZE, FONT_STYLE } from 'src/lib/player/constants';
import tinycolor from 'tinycolor2';

export type Bounds = {
  innerWidth: number;
  innerHeight: number;
  paddingTop: number;
  paddingRight: number;
  paddingLeft: number;
  paddingBottom: number;
};

export default function SVGChart({
  width,
  height,
  xAxis,
  yAxis,
  dimAxis = false,
  children,
  style,
  gestureBind,
  forwardRef,
  boundsRef,
  lineColor = '#ffffff',
  showGrid = true,
  showAxis = true,
  onDoubleClick,
}: {
  width: number;
  height: number;
  xAxis: { ticks: Array<[number, string]>; min: number; max: number };
  yAxis: { ticks: Array<[number, string]>; min: number; max: number };
  dimAxis?: boolean;
  children: (bounds: Bounds) => JSX.Element;
  style?: CSSProperties;
  gestureBind?: DOMAttributes<Element>;
  forwardRef?: MutableRefObject<SVGSVGElement> | Ref<SVGSVGElement>;
  boundsRef?: MutableRefObject<Bounds | null>;
  lineColor?: string;
  showGrid?: boolean;
  showAxis?: boolean;
  onDoubleClick?: MouseEventHandler<SVGSVGElement>;
}): JSX.Element {
  const paddingTop = 15;
  const paddingRight = 20;
  const paddingBottom = 20;
  const paddingLeft = 51;
  const lineColorDimmed = tinycolor(lineColor).setAlpha(0.2).toRgbString();
  const bounds = {
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    innerWidth: width - paddingLeft - paddingRight,
    innerHeight: height - paddingBottom - paddingTop,
  };
  if (boundsRef != null) {
    boundsRef.current = bounds;
  }
  return (
    <svg
      className="svg-chart"
      style={{ ...style, width, height }}
      ref={forwardRef}
      onDoubleClick={onDoubleClick}
      {...(gestureBind != null ? gestureBind : {})}
    >
      <title>Chart</title>
      {xAxis.ticks.map(([value, label]: [number, string]) => {
        const posX =
          bounds.paddingLeft +
          Math.round(((value - xAxis.min) / (xAxis.max - xAxis.min)) * bounds.innerWidth);
        return (
          <Fragment key={`gridlinex-${value}`}>
            {showAxis && (
              <text
                x={posX}
                y={bounds.paddingTop + bounds.innerHeight + bounds.paddingBottom - 3}
                style={{
                  fontWeight: FONT_STYLE,
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZE,
                  textAlign: 'right',
                  textAnchor: 'middle',
                  fill: dimAxis ? lineColorDimmed : lineColor,
                }}
              >
                {label}
              </text>
            )}
            {showGrid && (
              <line
                x1={posX}
                x2={posX}
                y1={bounds.paddingTop + bounds.innerHeight}
                y2={bounds.paddingTop + bounds.innerHeight + 5}
                stroke={lineColorDimmed}
                strokeWidth={1}
              />
            )}
          </Fragment>
        );
      })}
      {yAxis.ticks.map(([value, label]: [number, string]) => {
        const posY =
          yAxis.max === yAxis.min
            ? 0.5 * bounds.innerHeight
            : Math.round((1 - (value - yAxis.min) / (yAxis.max - yAxis.min)) * bounds.innerHeight);
        return (
          <Fragment key={`gridliney-${value}`}>
            {showAxis && (
              <text
                x={bounds.paddingLeft - 6}
                y={bounds.paddingTop + posY + 4}
                style={{
                  fontWeight: FONT_STYLE,
                  fontFamily: FONT_FAMILY,
                  fontSize: FONT_SIZE,
                  textAlign: 'right',
                  textAnchor: 'end',
                  fill: dimAxis ? lineColorDimmed : lineColor,
                }}
              >
                {label}
              </text>
            )}
            {showGrid && (
              <line
                x1={bounds.paddingLeft}
                x2={bounds.paddingLeft + bounds.innerWidth}
                y1={bounds.paddingTop + posY}
                y2={bounds.paddingTop + posY}
                stroke={lineColorDimmed}
                strokeWidth={1}
              />
            )}
          </Fragment>
        );
      })}
      {showAxis && (
        <line
          x1={bounds.paddingLeft}
          x2={bounds.paddingLeft + bounds.innerWidth}
          y1={bounds.paddingTop + bounds.innerHeight}
          y2={bounds.paddingTop + bounds.innerHeight}
          stroke={lineColorDimmed}
          strokeWidth={1}
        />
      )}
      {children(bounds)}
    </svg>
  );
}
